import { useState } from 'react';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

import { APP_LOCALES } from 'locale/locale.config';

import { TRACK_LABELS } from 'lib/tracking';
import { useLocale } from 'lib/localeProvider';

import { AppStore } from 'stores/AppStore';

import { colors, zLayers } from 'styles/theme';
import { tabletUp, mobileOnly } from 'styles/utils';

import Container from '../Container/Container';
import Breakpoint from '../Breakpoint/Breakpoint';
import Flexbox from '../Flexbox/Flexbox';
import CtaButton from '../CtaButton/CtaButton';

import NavbarLogo from './NavbarLogo';
import NavbarLinks from './NavbarLinks';
import Sidebar from './Sidebar';
import Hamburger from './Hamburger';
import {
  headerHeightDesktop,
  headerHeightMobile,
  headerHeightDesktopSm,
  headerHeightMobileSm,
  getNavbarLinks,
} from './Navbar.config';

const headerCss = ({ isSticky, topNoticeHeight }) => css`
  display: flex;
  align-items: center;
  background-color: ${isSticky ? colors.white : 'transparent'};
  box-shadow: ${isSticky && '0 2px 10px 0 rgba(0, 0, 0, 0.1)'};
  position: ${isSticky ? 'fixed' : 'absolute'};
  top: ${topNoticeHeight}px;
  left: 0;
  right: 0;
  transition: all 200ms ease;
  transition-property: background-color, height;
  z-index: ${zLayers.aboveAll};

  ${mobileOnly} {
    height: ${isSticky ? headerHeightMobileSm : headerHeightMobile}px;
  }

  ${tabletUp} {
    height: ${isSticky ? headerHeightDesktopSm : headerHeightDesktop}px;
  }
`;

const navCss = css`
  align-items: center;

  ${tabletUp} {
    align-items: flex-end;
  }
`;

const ctaLinkCss = css`
  color: ${colors.purple};
  margin-right: -4px;
`;

const hamburgerCss = css`
  margin-right: -11px;
`;

function Navbar({ currentSlug, topNotice }) {
  const { localeCode } = useLocale();
  const [isSticky, setIsSticky] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const topNoticeHeight = topNotice ? topNotice.current?.offsetHeight : 0;

  // Set the header to be sticky when not at the top of the page
  useScrollPosition(
    ({ currPos }) => {
      const notAtTop = currPos.y < 0;

      if (notAtTop !== isSticky) {
        setIsSticky(notAtTop);
      }
    },
    [isSticky]
  );

  const { navbarLinks, sidebarLinks } = getNavbarLinks();

  function toggleMenuOpen() {
    setIsMenuOpen(isMenuOpen => !isMenuOpen);
  }

  const isUkVelieve = localeCode === APP_LOCALES.UK && !AppStore.isDipUti;

  return (
    <header css={headerCss({ isSticky, topNoticeHeight })}>
      <Container>
        <Flexbox css={navCss} justifyContent="space-between" component="nav">
          {/* Logo */}
          <NavbarLogo isMobileLogoIcon={isSticky} />

          <Breakpoint gridUp>
            {!isUkVelieve && (
              <Flexbox alignItems="center">
                {/* Desktop links */}
                <NavbarLinks
                  isNavbarSticky={isSticky}
                  links={navbarLinks}
                  currentSlug={currentSlug}
                />
                {/* Desktop CTA */}
                <CtaButton
                  trackLabel={TRACK_LABELS.HEADER}
                  small
                  skin={isSticky ? 'contained' : 'outlined'}
                />
              </Flexbox>
            )}
          </Breakpoint>

          <Breakpoint gridDown>
            {!isUkVelieve && (
              <Flexbox alignItems="center">
                {/* Mobile CTA (when scrolled) */}
                {isSticky && (
                  <CtaButton
                    trackLabel={TRACK_LABELS.MOBILE_SCROLL}
                    css={ctaLinkCss}
                    skin="text"
                    small
                  />
                )}
                {/* Mobile hamburger icon button */}
                <Hamburger css={hamburgerCss} onClick={toggleMenuOpen} />
              </Flexbox>
            )}

            {/* Mobile sidebar */}
            {!isUkVelieve && (
              <Sidebar
                isOpen={isMenuOpen}
                onMenuClose={toggleMenuOpen}
                links={sidebarLinks}
                currentSlug={currentSlug}
              />
            )}
          </Breakpoint>
        </Flexbox>
      </Container>
    </header>
  );
}

Navbar.propTypes = {
  /** The current page's slug. For styling purposes */
  currentSlug: PropTypes.string,
  topNotice: PropTypes.object,
};

export default Navbar;
